// [NEW IMPLEMENTATION] Enablement Details Report
// # Command Line: Implemented Enablement/De-enablement status filters and record limit synchronization.
// Logic:
// 1. Provides granular user-level enablement data with date range and SKU filtering.
// 2. Calculates real-time summary counts (Total, Successful, Failed) for the selected user.
// 3. Supports multi-format exports (CSV, Excel, PDF) and dynamic pagination.
import axios from "axios";
import { Col, DatePicker, Input, Row, Select, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getAuthTokens, getTenantId } from "../../../redux/features/auth/authSlice";
import { baseURL, apiRoutes } from "../../../config/api";
import { useGetTrackNTraceTagQuery } from "../../../redux/services/trackntraceApiSlice";
import { useGetAllTenantProcessQuery } from "../../../redux/services/processApiSlice";
import { store } from "../../../redux/store";
import { downloadHtml2Pdf, removeEmptyKeys } from "../../../utils";
import ReportFiltersCard from "./ReportFiltersCard";
import { REPORT_TABLE_BODY_MAX_PX } from "./reportTableLayout";
import "./style.css";
import { exportRowsToWorkbook } from "./exportWorkbook";

const { RangePicker } = DatePicker;

const mapRow = (row: Record<string, unknown>) => {
  const status = String(row.status ?? "").toLowerCase();
  const isSuccess = status === "enabled" || status === "success";
  return {
    key: (row.diId ?? row.id ?? row._id ?? "") as string,
    siteName: (row.siteName ?? "") as string,
    productUPC: (row.productUPC ?? row.sku ?? "") as string,
    serial: (row.diId ?? (Array.isArray(row.epcList) ? row.epcList[0] : "") ?? "") as string,
    dateWritten: (row.operationTime ?? row.createdAt ?? "") as string,
    userName: (row.userName ?? "") as string,
    zone: (row.zoneName ?? row.deviceName ?? "") as string,
    success: isSuccess ? "Yes" : "No",
  };
};

const EnablementDetails = () => {
  const [searchParams] = useSearchParams();
  const userNameFromUrl = searchParams.get("user") ?? "";
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  const skuParam = searchParams.get("sku") ?? "";
  const processIdParam = searchParams.get("processId") ?? undefined;

  const tenantId = useSelector(getTenantId);

  const initialRange: [moment.Moment | null, moment.Moment | null] | null =
    fromParam && toParam
      ? [moment(fromParam, "YYYY-MM-DD"), moment(toParam, "YYYY-MM-DD")]
      : null;

  const [dateRange, setDateRange] =
    useState<[moment.Moment | null, moment.Moment | null] | null>(initialRange);
  const [sku, setSku] = useState(skuParam);
  const [processId, setProcessId] = useState<string | undefined>(processIdParam);
  const [appliedDateRange, setAppliedDateRange] =
    useState<[moment.Moment | null, moment.Moment | null] | null>(initialRange);
  const [appliedSku, setAppliedSku] = useState(skuParam);
  const [appliedProcessId, setAppliedProcessId] =
    useState<string | undefined>(processIdParam);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const detailsPdfRef = useRef<HTMLDivElement>(null);

  const applyFilters = useCallback(() => {
    setAppliedDateRange(dateRange);
    setAppliedSku(sku.trim());
    setAppliedProcessId(processId);
    setPage(1);
  }, [dateRange, sku, processId]);

  const resetFilters = useCallback(() => {
    setDateRange(null);
    setSku("");
    setProcessId(undefined);
    setAppliedDateRange(null);
    setAppliedSku("");
    setAppliedProcessId(undefined);
    setPage(1);
  }, []);

  const queryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        page,
        limit: pageSize,
        from: appliedDateRange?.[0] ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD") : undefined,
        to: appliedDateRange?.[1] ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD") : undefined,
        userName: userNameFromUrl || undefined,
        productUPC: appliedSku || undefined,
        processId: appliedProcessId || undefined,
      }),
    [tenantId, appliedDateRange, appliedSku, appliedProcessId, userNameFromUrl, page, pageSize]
  );

  const summaryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        page: 1,
        limit: 10000,
        from: appliedDateRange?.[0] ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD") : undefined,
        to: appliedDateRange?.[1] ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD") : undefined,
        userName: userNameFromUrl || undefined,
        productUPC: appliedSku || undefined,
        processId: appliedProcessId || undefined,
      }),
    [tenantId, appliedDateRange, appliedSku, appliedProcessId, userNameFromUrl]
  );

  const { data: processData } = useGetAllTenantProcessQuery(
    { tenantId, processType: "Digitization" },
    { skip: !tenantId }
  );
  const processList = useMemo(() => {
    const rows = processData?.result?.rows ?? processData?.data ?? [];
    return Array.isArray(rows)
      ? rows.map((p: { id: string; name?: string }) => ({ label: p.name ?? p.id, value: p.id }))
      : [];
  }, [processData]);

  const { data, isFetching } = useGetTrackNTraceTagQuery(queryParams, { skip: !tenantId || !userNameFromUrl });
  const { data: summaryData } = useGetTrackNTraceTagQuery(summaryParams, { skip: !tenantId || !userNameFromUrl });

  const result = useMemo(() => data?.result ?? data, [data]);
  const rows = useMemo(() => result?.rows ?? result?.data ?? [], [result]);
  const totalCount = result?.totalCount ?? result?.total ?? 0;
  const tableData = useMemo(() => (Array.isArray(rows) ? rows.map(mapRow) : []), [rows]);

  const summaryRows = useMemo(() => {
    const res = summaryData?.result ?? summaryData;
    const r = res?.rows ?? res?.data ?? [];
    return Array.isArray(r) ? r : [];
  }, [summaryData]);

  const { totalRecords, successful, failed } = useMemo(() => {
    let success = 0;
    let fail = 0;
    summaryRows.forEach((row: Record<string, unknown>) => {
      const status = String(row.status ?? "").toLowerCase();
      if (status === "enabled" || status === "success") success += 1;
      else fail += 1;
    });
    return {
      totalRecords: totalCount,
      successful: summaryRows.length > 0 ? success : totalCount,
      failed: summaryRows.length > 0 ? fail : 0,
    };
  }, [summaryRows, totalCount]);

  const columns: ColumnsType<ReturnType<typeof mapRow>> = useMemo(
    () => [
      { title: "Site", dataIndex: "siteName", key: "siteName", width: 120, sorter: true },
      { title: "SKU", dataIndex: "productUPC", key: "productUPC", width: 120, sorter: true },
      { title: "Tag ID", dataIndex: "serial", key: "serial", width: 140, sorter: true },
      {
        title: "Date",
        dataIndex: "dateWritten",
        key: "dateWritten",
        width: 100,
        sorter: true,
        render: (val: string) => (val ? moment(val).format("D MMM") : "-"),
      },
      { title: "Username", dataIndex: "userName", key: "userName", width: 120, sorter: true },
      { title: "Zone", dataIndex: "zone", key: "zone", width: 120, sorter: true },
      { title: "Success", dataIndex: "success", key: "success", width: 90, sorter: true },
    ],
    []
  );

  const getExportParams = useCallback(
    () =>
      removeEmptyKeys({
        tenantId,
        limit: 10000,
        page: 1,
        from: appliedDateRange?.[0] ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD") : undefined,
        to: appliedDateRange?.[1] ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD") : undefined,
        userName: userNameFromUrl || undefined,
        productUPC: appliedSku || undefined,
        processId: appliedProcessId || undefined,
      }),
    [tenantId, appliedDateRange, appliedSku, appliedProcessId, userNameFromUrl]
  );

  const fetchDataForExport = useCallback((params: Record<string, unknown>) => {
    const st = store.getState();
    const tokens = getAuthTokens(st) as {
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
    };
    const tid = getTenantId(st);
    return axios.get(`${baseURL}${apiRoutes.TRACK_N_TRACE_TAG}`, {
      params,
      headers: {
        "id-token": tokens?.idToken ?? "",
        "access-token": tokens?.accessToken ?? "",
        "refresh-token": tokens?.refreshToken ?? "",
        ...(tid ? { "tenant-id": tid } : {}),
      },
    });
  }, []);

  const exportCsv = useCallback(() => {
    if (!tenantId || !userNameFromUrl) {
      message.warning("No user selected.");
      return;
    }
    fetchDataForExport(getExportParams())
      .then((res) => {
        const result = res?.data?.result ?? res?.data;
        const rows = result?.rows ?? result?.data ?? [];
        const arr = Array.isArray(rows) ? rows.map(mapRow) : [];
        const header = ["Site", "SKU", "Tag ID", "Date", "Username", "Zone", "Success"];
        const csvContent = [
          header.join(","),
          ...arr.map((r) =>
            [
              r.siteName,
              r.productUPC,
              r.serial,
              r.dateWritten ? moment(r.dateWritten).format("D MMM") : "",
              r.userName,
              r.zone,
              r.success,
            ]
              .map((c) => `"${String(c).replace(/"/g, '""')}"`)
              .join(",")
          ),
        ].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `enablement-details-${userNameFromUrl}-${moment().format("YYYY-MM-DD")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        message.success("CSV exported.");
      })
      .catch(() => message.error("Export failed."));
  }, [tenantId, userNameFromUrl, getExportParams, fetchDataForExport]);

  const exportExcel = useCallback(() => {
    if (!tenantId || !userNameFromUrl) {
      message.warning("No user selected.");
      return;
    }
    fetchDataForExport(getExportParams())
      .then((res) => {
        const result = res?.data?.result ?? res?.data;
        const rows = result?.rows ?? result?.data ?? [];
        const arr = Array.isArray(rows) ? rows.map(mapRow) : [];
        const header = ["Site", "SKU", "Serial", "Date", "Username", "Zone", "Success"];
        const excelRows: Array<Array<string | number>> = [
          header,
          ...arr.map((r) => [
            r.siteName,
            r.productUPC,
            r.serial,
            r.dateWritten ? moment(r.dateWritten).format("D MMM") : "",
            r.userName,
            r.zone,
            r.success,
          ]),
        ];
        exportRowsToWorkbook(
          `enablement-details-${userNameFromUrl}-${moment().format("YYYY-MM-DD")}.xlsx`,
          "Enablement Details",
          excelRows
        );
        message.success("Excel exported.");
      })
      .catch(() => message.error("Export failed."));
  }, [tenantId, userNameFromUrl, getExportParams, fetchDataForExport]);

  const exportPdf = useCallback(() => {
    const el = detailsPdfRef.current;
    if (!el) {
      message.warning("Report content not ready.");
      return;
    }
    downloadHtml2Pdf(el, `enablement-details-${userNameFromUrl}-${moment().format("YYYY-MM-DD")}.pdf`);
  }, [userNameFromUrl]);

  const emailReport = useCallback(() => {
    message.info("Email report: configure backend endpoint or mailto to send this report.");
  }, []);

  if (!userNameFromUrl) {
    return (
      <div className="di-reports-page">
        <p className="details-missing-user">No user selected. Click a user in the chart on the Enablement Summary report.</p>
      </div>
    );
  }

  return (
    <div className="di-reports-page">
      <ReportFiltersCard defaultOpen>
        <Row gutter={[16, 16]} align="middle" className="filters-row">
          <Col className="filter-field-col">
            <span className="filter-label">Time Range ▼</span>
            <RangePicker
              value={dateRange ?? undefined}
              onChange={(dates) => setDateRange(dates as [moment.Moment | null, moment.Moment | null] | null)}
              allowClear
              className="filter-input-control"
            />
          </Col>
          <Col className="filter-field-col">
            <span className="filter-label">SKU ▼</span>
            <Input
              placeholder="Product UPC"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              allowClear
              className="filter-input-control"
            />
          </Col>
          <Col className="filter-field-col">
            <span className="filter-label">Process ▼</span>
            <Select
              placeholder="Process"
              value={processId || undefined}
              onChange={setProcessId}
              allowClear
              className="filter-input-control filter-select"
              options={processList}
            />
          </Col>
          <Col className="filters-inline-actions">
            <button type="button" className="btn-apply-filters" onClick={applyFilters}>
              Apply Filters
            </button>
            <button type="button" className="btn-reset-filters" onClick={resetFilters}>
              Reset
            </button>
          </Col>
        </Row>
      </ReportFiltersCard>

      <section className="enablement-table-section">
        <div className="table-card" ref={detailsPdfRef}>
          <div className="filters-section-title">Results</div>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={isFetching}
            className="uitable DITable diInfo"
            pagination={{
              current: page,
              pageSize,
              total: totalCount,
              hideOnSinglePage: false,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              onChange: (p, size) => {
                setPage(p);
                setPageSize(size ?? 25);
              },
            }}
            scroll={{ x: 1100, y: REPORT_TABLE_BODY_MAX_PX }}
            size="small"
          />
          <div className="details-summary">
            <div className="details-summary-counts">
              <span>Total Records: {totalRecords}</span>
              <span>Successful: {successful}</span>
              <span>Failed: {failed}</span>
            </div>
            <div className="report-actions actions">
              <button type="button" className="report-action-btn" onClick={exportCsv}>
                Export CSV
              </button>
              <button type="button" className="report-action-btn" onClick={exportExcel}>
                Export Excel
              </button>
              <button type="button" className="report-action-btn" onClick={exportPdf}>
                Export PDF
              </button>
              <button type="button" className="report-action-btn" onClick={emailReport}>
                Email Report
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnablementDetails;
